import React from 'react';
import {
    AppShell,
    Box,
    Burger,
    Divider,
    Group,
    Paper,
    useMantineTheme,
} from '@mantine/core';
import { NAVBAR_WIDTH, HEADER_HEIGHT } from '@/common/constants/constants';
import { useDisclosure } from '@mantine/hooks';
import SecondarySidebar from './SecondarySidebar';
import { useSecondarySidebarSync } from '@/common/hooks/useSecondarySidebarSync';

// 최소한의 프롭스만 정의하여 코어 컴포넌트 가볍게 유지
interface LayoutCoreProps {
    children: React.ReactNode;
    header: React.ReactNode;
    navbar: React.ReactNode;
    contentProps?: {
        noShadow?: boolean;
        noRadius?: boolean;
        style?: React.CSSProperties;
    };
}

/**
 * 레이아웃의 기본 구조만 담당하는 경량 코어 컴포넌트
 * 무거운 기능이나 로직 없이 순수하게 레이아웃 구조만 처리
 */
const LayoutCore = ({
    children,
    header,
    navbar,
    contentProps = {},
}: LayoutCoreProps) => {
    const theme = useMantineTheme();
    const { noShadow, noRadius, style } = contentProps;
    const [opened, { toggle }] = useDisclosure();

    // URL 변경에 따른 사이드바 상태 자동 업데이트
    useSecondarySidebarSync();

    return (
        <AppShell
            h="100%"
            header={{ height: HEADER_HEIGHT }}
            navbar={{
                width: NAVBAR_WIDTH,
                breakpoint: 'sm',
                collapsed: { mobile: !opened },
            }}
            style={{
                backgroundColor: theme.colors.gray[1],
                border: 'none',
            }}
        >
            <AppShell.Header
                style={{
                    backgroundColor: theme.colors.gray[1],
                    border: 'none',
                }}
            >
                <Group gap={0}>
                    <Burger
                        opened={opened}
                        onClick={toggle}
                        hiddenFrom="sm"
                        size={14}
                        style={{
                            padding: '.5rem 0 0 .75rem',
                        }}
                    />
                    <Box flex={1}>{header}</Box>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar
                style={{
                    backgroundColor: theme.colors.gray[1],
                    border: 'none',
                }}
            >
                {navbar}
            </AppShell.Navbar>

            <AppShell.Main style={{ height: 'calc(100% - 60px)' }}>
                <div style={{ padding: '.5rem', height: '100%' }}>
                    <Paper
                        shadow={noShadow ? 'none' : 'md'}
                        radius={noRadius ? 'none' : 'lg'}
                        style={{
                            backgroundColor: '#ffffff',
                            height: '100%',
                            minWidth: '1024px',
                            overflow: 'hidden',
                            overflowX: 'auto',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'row',
                            ...style,
                        }}
                    >
                        <SecondarySidebar />
                        <Divider orientation="vertical" />
                        <div
                            style={{
                                flex: 1,
                                minWidth: 0, // flex 아이템이 줄어들 수 있게 함
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{ height: '100%', overflow: 'auto' }}>
                                {children}
                            </div>
                        </div>
                    </Paper>
                </div>
            </AppShell.Main>
        </AppShell>
    );
};

export default React.memo(LayoutCore);
